import { useContext, useEffect, useMemo, useState } from "react";
import { getBankInfo, updateBankInfo } from "../services/configurations/banks";
import {
  InitialBankInfoC,
  Globalization,
  Language,
} from "../interfaces/BDModels";
import { AppContext } from "../context";

interface BankInfo {
  appInfo: {
    id: number;
    name: string;
    segment: string;
    status: string;
  };
  languages: LanguageOptions[];
  dateFormat: {
    [key: string]: string | number | boolean;
  };
  currencyInfo: Globalization;
}

interface LanguageOptions extends Language {
  name: string;
}

export const useBank = () => {
  const {
    t,
    toast,
    appState: { selectedApp },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<BankInfo>();
  const appid = selectedApp?.appid || "0";

  const currencyKeys = useMemo(() => {
    return  [
      "groupSeparator",
      "decimalSeparator",
      "decimalNumber",
      "showCurrencyCode",
      "defaultCurrency",
    ];
  }, [])

  const bankUpdate = async (data: any) => {
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(updateBankInfo(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: t("InitialConfiguration.apiUpdateConfirmationLabel"),
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (appid) {
      (async function () {
        await Promise.resolve(getBankInfo(appid))
          .then((response) => {
            const bank: InitialBankInfoC = response?.data;
            setData({
              appInfo: {
                id: bank.application.id,
                name: bank.application.name,
                segment: bank.application.segment,
                status: bank.application.status,
              },
              languages: bank.language.map((lan) => ({
                ...lan,
                name: t(`Languages.${lan.isoCode}`),
              })),
              dateFormat: Object.fromEntries(
                Object.entries(bank.globalization).filter(
                  ([key, _]) => key === "overrideDateFormat"
                )
              ),
              currencyInfo: Object.fromEntries(
                Object.entries(bank.globalization).filter(([key, _]) =>
                  currencyKeys.includes(key)
                )
              ) as Globalization,
            });
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, t, currencyKeys]);

  return {
    ...data,
    bankUpdate,
    currencyKeys
  };
};
